import { createFileRoute, redirect } from "@tanstack/react-router";

import { PageHeader } from "../components/shell/app-shell";
import { Button } from "../components/ui/button";
import { Card, CardHeader } from "../components/ui/card";
import { Meter } from "../components/ui/meter";
import { Metric } from "../components/ui/metric";
import { Badge } from "../components/ui/pill";
import { Table, type TableColumn } from "../components/ui/table";
import { CAP_TABLE, type CapTableEntry } from "../demo-data/equity";
import { COMING_SOON_SECTIONS } from "../domain/sections";
import { formatInteger } from "../lib/format";

export const Route = createFileRoute("/equity")({
	// Still to come (COMING_SOON_SECTIONS): whoever opens this page, by a link or by hand, lands on Home instead.
	beforeLoad: () => {
		if (COMING_SOON_SECTIONS.has("equity")) {
			throw redirect({ to: "/", replace: true });
		}
	},
	head: () => ({ meta: [{ title: "Equity & Investors · Ravnar" }] }),
	staticData: { crumbs: ["Equity & Investors"] },
	component: EquityPage,
});

const columns: readonly TableColumn<CapTableEntry>[] = [
	{ key: "holder", header: "Holder", cell: entry => <span className="font-medium">{entry.holder}</span> },
	{ key: "instrument", header: "Instrument", cell: entry => <Badge>{entry.instrument}</Badge> },
	{ key: "shares", header: "Shares", align: "right", cell: entry => formatInteger(entry.shares) },
	{
		key: "ownership",
		header: "Ownership",
		align: "right",
		cell: entry => (
			<span className="flex items-center justify-end gap-2.5">
				<span className="w-[96px]">
					<Meter value={entry.percent} />
				</span>
				<span className="w-[46px] text-right">{entry.percent.toFixed(1)}%</span>
			</span>
		),
	},
];

const sharesOutstanding = CAP_TABLE.reduce((total, entry) => total + entry.shares, 0);

function EquityPage() {
	return (
		<>
			<PageHeader
				title="Equity & Investors"
				description="Who owns what, and what has to be reported to them."
				actions={
					<>
						<Button>Investor update</Button>
						<Button variant="primary">Issue shares</Button>
					</>
				}
			/>
			<div className="mb-[22px] grid grid-cols-1 gap-[11px] md:grid-cols-3">
				<Metric label="Shares outstanding" value={formatInteger(sharesOutstanding)} sub="Fully diluted" />
				<Metric label="Holders of record" value="4" sub="1 institutional" />
				<Metric label="Pending actions" value="0" sub="Nothing needs you" tone="ok" />
			</div>
			<Card>
				<CardHeader
					title="Cap table"
					action={<span className="text-caption text-ink-3">As of Sep 13, 2026</span>}
				/>
				<Table label="Cap table" columns={columns} rows={CAP_TABLE} rowKey={entry => entry.id} />
			</Card>
		</>
	);
}
